import { Component, Input, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ButtonComponent, PhotoComponent } from 'wecheer-common';
import { Advertiser, AdvertiserLanguageDto, CampaignsAdvertiserAdminApiClient, CreateAdvertiserRequestDto } from 'src/app/api/api-campaigns.service.generated';
import { MatSnackBar } from '@angular/material/snack-bar';
import { S3Upload } from 'src/app/common/utils/S3Upload';
import { GUID } from 'src/app/common/utils/GuidUtils';
import { lastValueFrom, Observable, of, Subject } from 'rxjs';
import { MultiLabelsComponent } from 'src/app/common/components/multi-labels/multi-labels.component';
import { CountryCode } from 'src/app/advertiser/advertiser-details/advertiser-campaigns/_/campaign-base-edit/country-code';
import { getSuggestedLanguages, getAllLanguages, LanguageItem } from 'src/app/common/utils/language-mapping.utils';

interface Country {
  productId: string;
  name: string;
  iso2: string;
  dialCode: string;
  priority: number;
  areaCodes: string[] | undefined;
  flagClass: string;
  placeHolder: string;
}

@Component({
  selector: 'app-advertiser-general-info',
  standalone: true,
  templateUrl: './advertiser-general-info.component.html',
  styleUrls: ['./advertiser-general-info.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    ButtonComponent,
    PhotoComponent,
    MultiLabelsComponent
]
})
export class AdvertiserGeneralInfoComponent implements OnInit, OnChanges {
  @Input() advertiser!: Advertiser;

  form: FormGroup;
  photoUrl: string = '';
  isLoading = false;
  uploadPhotoKey: string;
  ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png'];

  // Country & Language fields
  allCountries: Observable<Country[]> = of([]);
  filteredCountries: Country[] = [];
  allLanguages: Observable<LanguageItem[]> = of([]);
  filteredLanguages: LanguageItem[] = [];
  availableLanguagesForPrimary: LanguageItem[] = [];
  refreshCountries = new Subject<string>();
  refreshLanguages = new Subject<string>();

  private formBuilder = inject(FormBuilder);
  private client = inject(CampaignsAdvertiserAdminApiClient);
  private snackBar = inject(MatSnackBar);

  ngOnInit() {
    this.initializeForm();
    this.allCountries = of(this.fetchCountryData());
    this.allLanguages = of(getAllLanguages());
    // Update form if advertiser is already available
    if (this.advertiser) {
      this.updateFormFromAdvertiser();
    }
    // Trigger autocomplete refresh
    setTimeout(() => {
      this.refreshCountries.next('');
      this.refreshLanguages.next('');
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['advertiser'] && this.form) {
      this.updateFormFromAdvertiser();
    }
  }

  private initializeForm() {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(50)]],
      description: ['', Validators.maxLength(100)],
      appToRedirect: ['', Validators.maxLength(100)],
      countries: [[]],
      languages: [[]],
      primaryLanguage: ['']
    });
  }

  private updateFormFromAdvertiser() {
    if (this.advertiser) {
      // Map countries to Country objects for multi-labels
      const countryData = this.fetchCountryData();
      const selectedCountries = (this.advertiser.countries || [])
        .map(iso2 => countryData.find(c => c.iso2 === iso2))
        .filter(c => !!c) as Country[];

      // Map languages to LanguageItem objects for multi-labels
      const selectedLanguages: LanguageItem[] = (this.advertiser.languages || [])
        .map(l => ({ code: l.code!, name: l.name! }));

      this.filteredCountries = selectedCountries;
      this.filteredLanguages = selectedLanguages;
      this.availableLanguagesForPrimary = selectedLanguages;

      this.form.patchValue({
        name: this.advertiser.name || '',
        description: this.advertiser.description || '',
        appToRedirect: this.advertiser.appToRedirect || '',
        countries: selectedCountries,
        languages: selectedLanguages,
        primaryLanguage: this.advertiser.primaryLanguage || ''
      });
      this.photoUrl = this.advertiser.photoThumb || '';
    }
  }

  onCountriesChanged(countries: Country[]) {
    this.filteredCountries = countries;
    // Auto-suggest languages from selected countries
    const countryCodes = countries.map(c => c.iso2);
    const suggestedLanguages = getSuggestedLanguages(countryCodes);

    // Merge with existing languages (don't remove manually added ones)
    const currentLanguages: LanguageItem[] = this.form.get('languages')?.value || [];
    const merged = [...currentLanguages];
    for (const suggested of suggestedLanguages) {
      if (!merged.some(l => l.code === suggested.code)) {
        merged.push(suggested);
      }
    }
    this.filteredLanguages = merged;
    this.availableLanguagesForPrimary = merged;
    this.form.patchValue({ languages: merged });

    // Set primary language if not set
    if (!this.form.get('primaryLanguage')?.value && merged.length > 0) {
      this.form.patchValue({ primaryLanguage: merged[0].code });
    }
  }

  onLanguagesChanged(languages: LanguageItem[]) {
    this.filteredLanguages = languages;
    this.availableLanguagesForPrimary = languages;
    // If primary language is removed from list, reset it
    const primaryLang = this.form.get('primaryLanguage')?.value;
    if (primaryLang && !languages.some(l => l.code === primaryLang)) {
      this.form.patchValue({ primaryLanguage: languages.length > 0 ? languages[0].code : '' });
    }
  }

  private fetchCountryData(): Country[] {
    return new CountryCode().allCountries.map(c => {
      return {
        productId: c[1].toString(),
        name: c[0].toString(),
        iso2: c[1].toString(),
        dialCode: c[2].toString(),
        priority: +c[3] || 0,
        areaCodes: c[4] as string[] || undefined,
        flagClass: c[1].toString().toLocaleLowerCase(),
        placeHolder: ''
      };
    });
  }

  async onAvatarImageSelected(event: any) {
    try {
      const file = event.target.files[0];
      if (!file || !this.isValidFile(file)) {
        this.snackBar.open('Invalid image file', 'Dismiss', { duration: 2000 });
        return;
      }

      this.uploadPhotoKey = GUID.newGuid().replace(/-/g, '');
      const uploadUrl = await lastValueFrom(this.client.getAdvertiserUploadUrl(this.uploadPhotoKey));
      const uploadSuccess = await lastValueFrom(S3Upload.upload(uploadUrl, file));
      if (uploadSuccess) {
        const parts = uploadUrl.split('?');
        const photoUrl = parts[0];
        this.photoUrl = photoUrl.replace('s3.ap-southeast-1.amazonaws.com/', ''); // trim s3 part to get photo using cloudfront
      }
    } catch (err) {
      console.error('upload failed', err);
    }
  }

  isValidFile(file: File): boolean {
    return file.name && this.ALLOWED_EXTENSIONS.filter(e => file.name.trim().toLowerCase().endsWith(e)).length > 0;
  }

  async onSave() {
    if (this.form.valid && this.advertiser) {
      this.isLoading = true;
      try {
        const formData = this.form.value;

        // Map country objects to ISO2 codes
        const countryCodes = (formData.countries || []).map((c: Country) => c.iso2);
        // Map language objects to AdvertiserLanguageDto
        const languageDtos = (formData.languages || []).map((l: LanguageItem) =>
          new AdvertiserLanguageDto({ code: l.code, name: l.name })
        );

        const model = new CreateAdvertiserRequestDto({
          advertiser: new Advertiser({
            ...this.advertiser,
            name: formData.name,
            description: formData.description,
            appToRedirect: formData.appToRedirect,
            countries: countryCodes,
            languages: languageDtos,
            primaryLanguage: formData.primaryLanguage
          }),
          uploadKey: this.uploadPhotoKey
        });

        if (model.advertiser.name.toString().trim() === '') {
          this.snackBar.open(`Invalid advertiser name`, null, { duration: 3000 });
          return;
        }

        await lastValueFrom(this.client.createOrUpdateAdvertiser(model));
        this.snackBar.open(`Advertiser ${model.advertiser.name} was saved`, null, { duration: 3000 });

        // Reset upload key after successful save
        this.uploadPhotoKey = '';

      } catch (error) {
        console.error('Error updating advertiser:', error);
        this.snackBar.open('Error updating advertiser', 'Close', { duration: 3000 });
      } finally {
        this.isLoading = false;
      }
    }
  }
}
